import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

interface AuthData {
  identifier: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private socket!: Socket;
  private reconnectData?: AuthData; // 🔹 guarda la última data usada para reconexión

  constructor() { // 🔹 Al instanciar el servicio, intentamos cargar credenciales previas
    this.restoreReconnectData();
    // 🔹 Y tratamos de conectar inmediatamente
    this.connect();
  }

  private restoreReconnectData(): void {
    try {
      const raw = localStorage.getItem('reconnectData');
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (parsed?.identifier && parsed?.name) {
        this.reconnectData = parsed as AuthData;
      } else {
        console.warn('⚠️ reconnectData inválido en storage, limpiando.');
        localStorage.removeItem('reconnectData');
      }
    } catch (e) {
      console.error('⚠️ Error parseando reconnectData del storage:', e);
      localStorage.removeItem('reconnectData');
    }
  }

  connect(data?: AuthData): void {
    // si ya existe conexión, no reconectamos
    if (this.socket && this.socket.connected) {
      return;
    }

    // 🔹 guarda credenciales para posible reconexión automática
    if (data) {
      this.reconnectData = data;
      localStorage.setItem("reconnectData", JSON.stringify(this.reconnectData))
    }

    // si no hay data ni guardada, no puede conectarse
    let authPayload = this.reconnectData;
    if (!authPayload) {
      const authPayloadStorage = localStorage.getItem("reconnectData")
      const authPayloadStorageJson = JSON.parse(authPayloadStorage)
      if(!authPayloadStorageJson){
        console.error('❌ No hay datos de autenticación para conectar el WebSocket');
        return;
      }
      authPayload = authPayloadStorageJson
    }

    // crea el socket
    this.socket = io(environment.apis.socketUrl, {
      transports: ['websocket'],
      auth: {
        identifier: authPayload.identifier,
        name: authPayload.name
      }
    });

    this.registerDefaultHandlers();
  }

  private registerDefaultHandlers(): void {
    this.socket.on('connect', () => {
      console.log('✅ Conectado al servidor:', this.socket.id);
    });

    this.socket.on('unauthorized', (data) => {
      console.error('🚫 Desconectado: ', data.reason);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Desconectado del servidor. Motivo:', reason);

      // 🔁 intento de reconexión automático
      this.tryReconnect();
    });
  }

  private tryReconnect(): void {
    // Espera 2 segundos antes de intentar reconectar
    const authPayloadStorage = localStorage.getItem("reconnectData")
    const authPayloadStorageJson = JSON.parse(authPayloadStorage)
    if (authPayloadStorageJson) {
      console.log('🔄 Intentando reconexión automática...');
      setTimeout(() => {
        this.connect(); // vuelve a conectar con los datos guardados
      }, 500);
    } else {
      console.warn('⚠️ No hay datos guardados para reconectar.');
    }
  }

  disconnect(): void {
    if (this.socket && this.socket.connected) {
      console.log('🔌 Cerrando conexión WebSocket...');
      this.reconnectData = null;
      const authPayloadStorage = localStorage.getItem("reconnectData")
      const authPayloadStorageJson = JSON.parse(authPayloadStorage)
      console.log(authPayloadStorageJson)
      this.socket.disconnect();
    }
  }

  /** 🔹 Quita un listener específico para evitar duplicados */
  off(event: string): void {
    if (this.socket) {
      this.socket.off(event);
      console.log(`🧹 Listener eliminado`);
    }
  }

  // 🔹 Emitir eventos
  emit(event: string, ...args: any[]): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, ...args);
    } else {
      console.warn(`⚠️ No se pudo emitir '${event}', socket no conectado.`);
      this.connect()
    }
  }

  // 🔹 Escuchar eventos
  listen<T>(event: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      this.socket.on(event, (data: T) => {
        subscriber.next(data);
      });
    });
  }
}
